import type { ResultOf } from '@graphql-typed-document-node/core';
import type { AdminTopicListItem, ExploreTopicCard } from '~kui/components/KUI/Topic/Card/types';

import type { fetchAdminLearningAssets } from '~/graphql/documents/learn/fetchLearningAssets';
import type { FragmentType } from '~/graphql/generated/fragment-masking';
import type { AdminLearningAssetCollectionFieldsFragment, LearningOutcome, LearningOutcomeInput } from '~/graphql/generated/graphql';
import type { CoinValue, LearningContent, SectionContentItem, SeoMeta, StructuredLearningOutcome } from '~/types/Learn/learningContent';

import { coinValueFragment } from '~/graphql/documents/content';
import { blockUnionFragment, learningAssetContentDataFragment, learningContentFragment, seoMetaFragment } from '~/graphql/documents/learningContent';
import { useFragment } from '~/graphql/generated/fragment-masking';
import { toAdminCurriculumTagList } from '~/utils/learn/curriculumTags';
import { transformAdminContentFile } from '~/utils/transformers/fileTransformers';

export { transformPublicFile } from './fileTransformers';

export const transformCoinValue = (data: FragmentType<typeof coinValueFragment> | null | undefined): CoinValue | null => {
  if (!data) {
    return null;
  }
  return useFragment(coinValueFragment, data);
};

export const transformSeoMeta = (data: FragmentType<typeof seoMetaFragment> | null | undefined): SeoMeta | null => {
  if (!data) {
    return null;
  }

  return useFragment(seoMetaFragment, data);
};

export const transformLearningContent = (data: FragmentType<typeof learningContentFragment> | null | undefined): LearningContent | null => {
  if (!data) {
    return null;
  }

  const contentData = useFragment(learningContentFragment, data);
  const dataFragmentData = useFragment(learningAssetContentDataFragment, contentData.data);

  return {
    ...contentData,
    data: dataFragmentData.map(
      section =>
        <SectionContentItem>{
          ...section,
          learningAssetContentItems: section.learningAssetContentItems.map(item => useFragment(blockUnionFragment, item)),
        },
    ),
  };
};

export const serializeStructuredLearningOutcome = (outcome: StructuredLearningOutcome | null | undefined): LearningOutcomeInput | null => {
  if (!outcome) {
    return null;
  }
  return {
    title: outcome.title,
    description: outcome.description ? JSON.stringify(outcome.description) : null,
  };
};

export const deserializeStructuredLearningOutcome = (raw: LearningOutcome | null | undefined): StructuredLearningOutcome | null => {
  if (!raw) {
    return null;
  }
  let description: unknown = null;
  if (typeof raw.description === 'string') {
    try {
      description = JSON.parse(raw.description);
    }
    catch (error) {
      console.error('[deserializeStructuredLearningOutcome] Failed to parse description JSON', { value: raw.description, error });
      description = null;
    }
  }
  else {
    description = raw.description ?? null;
  }
  return { title: raw.title, description };
};

/**
 * The explore card off the admin projection, where a collection's assets arrive as
 * `AdminLearningAsset` rows: the cover is an `AdminFile` fragment rather than an inline selection,
 * and there is no viewer progress to read.
 */
export const transformToAdminLearningAssetCard = (
  data: NonNullable<AdminLearningAssetCollectionFieldsFragment['learningAssets']>[number],
): ExploreTopicCard => {
  const coverImage = transformAdminContentFile(data.coverImage);
  return {
    id: data.id,
    image: {
      src: coverImage?.url ?? '',
      alt: coverImage?.name ?? '',
    },
    coins: transformCoinValue(data.coinValue)?.coins ?? 0,
    name: data.name,
    slug: data.slug,
    duration: data.duration ?? 0,
    completed: false,
    channels: null,
    baseScore: null,
    finalScore: null,
  };
};

export const transformToAdminLearningAssetListItem = (
  data: ResultOf<typeof fetchAdminLearningAssets>['learningAssetsList']['data'][number],
  dateFormat?: string,
): AdminTopicListItem => {
  const coinValue = transformCoinValue(data.coinValue);
  return {
    id: data.id,
    coins: coinValue?.coins ?? 0,
    name: data.name,
    duration: data.duration ?? 0,
    internalName: data.internalName ?? 'N/A',
    templateName: data.templateName ?? 'N/A',
    createdAt: data.createdAt ? formatDateTimeToUserLocale(data.createdAt, dateFormat) : null,
    publishedAt: data.publishedAt ? formatDateTimeToUserLocale(data.publishedAt, dateFormat) : null,
    publishedContentId: data.publishedContentId ?? null,
    draftContentId: data.draftContentId ?? null,
    type: data.type,
    curriculumTags: toAdminCurriculumTagList(data.curriculumTags),
  };
};
